import { RecipientRecord, IRecipientData, recipientFromJSON } from "common/records/Recipient";
import { Record, List, Map, fromJS } from "immutable";
import { Nullable } from "common/records";
import { IDocumentData } from "common/records/Document";
import { checkDate } from "common/helpers/Date";

export const signedDocumentFromJSON = (json: Partial<ISignedDocumentData>): SignedDocumentRecord => {
  const record: ISignedDocumentRecord = { ...(json as any) };

  if (json.recipients) {
    record.recipients = List(
      json.recipients.map((r) => {
        return recipientFromJSON(r);
      }),
    );
  }

  if (json.metadata) {
    record.metadata = fromJS(json.metadata);
  }

  if (json.estimate) {
    record.estimate = fromJS(json.estimate);
  }

  if (json.job) {
    record.job = fromJS(json.job);
  }

  if (json.completed_at) {
    record.completed_at = checkDate(json.completed_at);
  }

  if (json.created_at) {
    record.created_at = checkDate(json.created_at);
  }

  if (json.updated_at) {
    record.updated_at = checkDate(json.updated_at);
  }

  return new SignedDocumentRecord(record);
};

export const signedDocumentDestination = (signedDocument: SignedDocumentRecord): Nullable<string> => {
  if (signedDocument.kind === "contract") {
    const jobId = signedDocument.estimate?.get("job_id");
    return jobId ? `/jobs/${jobId}/signatures/estimate_documents` : null;
  }

  if (signedDocument.kind === "document") {
    const jobId = signedDocument.job?.get("id");
    return jobId ? `/jobs/${jobId}/signatures/signable_documents` : null;
  }

  return null;
};

export const signedDocumentEstimateName = (signedDocument: SignedDocumentRecord): string =>
  signedDocument.estimate?.get("name") ?? "";

export const signedDocumentJobName = (signedDocument: SignedDocumentRecord): string =>
  signedDocument.job?.get("name") ?? "";

export interface ISignedDocumentEstimateData {
  id: number;
  name: string;
  job_id: Nullable<number>;
}

export interface ISignedDocumentJobData {
  id: number;
  name: string;
}

export interface ISignedDocumentData {
  id: number;
  estimate_id: number;
  recipients: Array<Partial<IRecipientData>>;
  status: string;
  url: string;
  platform: string;
  metadata: any;
  document: Partial<IDocumentData>;
  document_id: Nullable<number>;
  signed_document_id: Nullable<number>;
  validation_status: Nullable<number>;
  message: string;
  loading?: boolean;
  completed_at: string;
  // Index-row fields (`GET /api/v1/signed_documents` with `include[]=estimate,job`) — absent on show/create responses.
  kind?: string;
  created_at?: string;
  updated_at?: string;
  estimate?: Partial<ISignedDocumentEstimateData>;
  job?: Partial<ISignedDocumentJobData>;
}

export interface ISignedDocumentRecord {
  id: number;
  estimate_id: number;
  status: string;
  url: string;
  document_id: number;
  signed_document_id: number;
  recipients: List<RecipientRecord>;
  platform: string;
  metadata: Map<string, any>;
  loading: boolean;
  validation: Nullable<Map<string, any>>;
  validation_status: Nullable<number>;
  message: string;
  completed_at: Nullable<Date>;
  kind: string;
  created_at: Nullable<Date>;
  updated_at: Nullable<Date>;
  estimate: Nullable<Map<string, any>>;
  job: Nullable<Map<string, any>>;
}

export const defaultSignedDocumentProps = {
  id: 0,
  estimate_id: 0,
  status: "",
  url: "",
  recipients: List<RecipientRecord>(),
  loading: false,
  platform: "scribble",
  metadata: Map<string, any>(),
  document_id: 0,
  signed_document_id: 0,
  validation: null,
  validation_status: null,
  message: "",
  completed_at: null,
  kind: "",
  created_at: null,
  updated_at: null,
  estimate: null,
  job: null,
};

export class SignedDocumentRecord
  extends Record<ISignedDocumentRecord>(defaultSignedDocumentProps)
  implements ISignedDocumentRecord {}
